import { useState } from 'react';
import { forWeight, searchFoods, type Food } from '@st/foods';
import { useTranslation } from '../../i18n/I18nProvider';
import { Button, Input, NumberInput } from '../../ui';

export interface FoodPickerProps {
  /** Dodaje se NA postojeće brojke — obrok je gotovo uvijek više sastojaka. */
  onAdd: (food: Food, grams: number) => void;
}

/**
 * Namirnica po gramima.
 *
 * Popis sirovih namirnica postoji zato što gotova i složena jela rješavaju
 * druge dvije stvari — prijedlozi iz ranijih obroka i trenerovi predlošci.
 * Ovdje se rješava ono što one ne pokrivaju: klijent zna da je pojeo 150 g
 * piletine i 200 g riže, a ne zna koliko je to kalorija.
 *
 * Pretraga radi lokalno, bez ijednog zahtjeva prema serveru — popis putuje s
 * aplikacijom, pa radi i kad mreže nema.
 */
export function FoodPicker({ onAdd }: FoodPickerProps) {
  const { t, locale } = useTranslation();
  const [upit, setUpit] = useState('');
  const [odabrana, setOdabrana] = useState<Food | null>(null);
  const [grami, setGrami] = useState<number | ''>('');

  const naziv = (food: Food) => (locale === 'en' ? food.en : food.hr);
  const rezultati = odabrana ? [] : searchFoods(upit, 6);

  function dodaj() {
    if (!odabrana || grami === '' || grami <= 0) return;
    onAdd(odabrana, grami);
    setOdabrana(null);
    setUpit('');
    setGrami('');
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-bg-alt p-3">
      <span className="text-[11.5px] uppercase tracking-[0.08em] text-ink-muted">
        {t('nutrition.foodsTitle')}
      </span>

      {odabrana ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate text-[14.5px] text-ink">{naziv(odabrana)}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOdabrana(null);
                setGrami('');
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[9rem] flex-1">
              <NumberInput
                id="food-grams"
                label={t('nutrition.foodsGrams')}
                suffix="g"
                min={1}
                value={grami}
                onValueChange={setGrami}
              />
            </div>
            <Button size="sm" onClick={dodaj} disabled={grami === '' || grami <= 0}>
              {t('nutrition.foodsAdd')}
            </Button>
          </div>

          {/* Rezultat prije dodavanja — inače se brojka pojavi u obrascu i ne
              zna se odakle je došla. */}
          <span className="text-[12px] tabular-nums text-ink-muted">
            {t('nutrition.foodsResult', {
              kcal: forWeight(odabrana, grami === '' ? 0 : grami).kcal,
              protein: forWeight(odabrana, grami === '' ? 0 : grami).proteinG,
            })}
          </span>
        </div>
      ) : (
        <>
          <Input
            id="food-search"
            label={t('nutrition.foodsSearch')}
            placeholder={t('nutrition.foodsSearchHint')}
            value={upit}
            onChange={(e) => setUpit(e.target.value)}
          />
          {rezultati.length > 0 && (
            <ul className="flex flex-col gap-1">
              {rezultati.map((food) => (
                <li key={food.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-surface"
                    onClick={() => {
                      setOdabrana(food);
                      // Očita porcija (jaje, kriška kruha) kao prijedlog; inače 100 g.
                      setGrami(food.portionG ?? 100);
                    }}
                  >
                    <span className="min-w-0 truncate text-[14px] text-ink">{naziv(food)}</span>
                    <span className="shrink-0 text-[12px] tabular-nums text-ink-muted">
                      {t('nutrition.foodsPer100', { kcal: food.kcal })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
